import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InfoModal = ({ isOpen, onClose }) => {
  const [typesConfig, setTypesConfig] = useState([]);
  const [levelsConfig, setLevelsConfig] = useState([]);

  useEffect(() => {
    if (isOpen) {
      Promise.all([
        axios.get(`${API}/config/types`),
        axios.get(`${API}/config/levels`)
      ]).then(([typesRes, levelsRes]) => {
        setTypesConfig(typesRes.data);
        setLevelsConfig(levelsRes.data);
      }).catch(() => {});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(44, 36, 23, 0.4)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="w-full max-w-md rounded-2xl shadow-2xl p-6 mb-4 sm:mb-0 max-h-[80vh] overflow-y-auto"
          style={{ backgroundColor: '#FFF8F0' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-lg text-charcoal">How it works</h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-charcoal/5">
              <X size={18} className="text-charcoal/40" />
            </button>
          </div>

          {/* Types section */}
          <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-3">Conversation Types</p>
          <div className="space-y-2.5 mb-6">
            {typesConfig.map((item) => (
              <div key={item.key} className="flex gap-3">
                <div className="w-1 rounded-full flex-shrink-0 mt-1" style={{ background: '#ff780a', opacity: 0.4, minHeight: '1rem' }} />
                <div>
                  <span className="text-charcoal font-medium text-sm">{item.label}</span>
                  <span className="text-charcoal/50 text-sm"> — {item.description}</span>
                  {item.use_cases && <p className="text-charcoal/40 text-xs mt-0.5">{item.use_cases}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Depth levels section */}
          <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-3">Depth Levels</p>
          <div className="space-y-2.5">
            {levelsConfig.map((item) => (
              <div key={item.key} className="flex gap-3">
                <div className="w-1 rounded-full flex-shrink-0 mt-1" style={{ background: '#2C2417', opacity: 0.15, minHeight: '1rem' }} />
                <div>
                  <span className="text-charcoal font-medium text-sm">{item.label}</span>
                  <span className="text-charcoal/50 text-sm"> — {item.description}</span>
                  {item.use_cases && <p className="text-charcoal/40 text-xs mt-0.5">{item.use_cases}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Learn more */}
          <div className="mt-6 pt-4 text-center" style={{ borderTop: '1px solid rgba(44,36,23,0.06)' }}>
            <Link
              to="/about"
              onClick={onClose}
              className="text-xs transition-colors"
              style={{ color: 'rgba(44,36,23,0.35)', textDecoration: 'underline', textUnderlineOffset: '3px' }}
              data-testid="info-learn-more"
            >
              Learn more about this app →
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InfoModal;
